import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { exitPlyxStandalone } from "@/lib/plyxStandalone";

/**
 * Plyx-branded exit-preview modal. Replaces the native window.confirm() so the
 * preview-exit flow stays on-brand AND captures lightweight NPS-style feedback
 * during the plyx.co.uk soft-launch period. All fire-and-forget — never blocks
 * the exit if the feedback POST fails.
 */
const REASONS = [
  "Just exploring",
  "Looks great — back to AfriMarket",
  "Confused / hard to use",
  "Missing a feature I need",
  "Other",
];

const PlyxExitModal = ({ open, onClose }) => {
  const [reason, setReason] = useState(null);
  const [score, setScore] = useState(null);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const submitAndExit = () => {
    // Fire-and-forget feedback POST; the exit itself never waits on it.
    try {
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      if (apiBase && (reason || score != null)) {
        fetch(`${apiBase}/api/plyx/exit-feedback`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            reason: reason || null,
            score: score,
            referrer: document.referrer || null,
          }),
          keepalive: true,  // ensure the POST survives navigation
        }).catch(() => { /* never block exit */ });
      }
    } catch { /* never block exit */ }
    exitPlyxStandalone();
    window.location.assign("/");
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      data-testid="plyx-exit-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plyx-exit-title"
    >
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md rounded-2xl p-6 border border-white/10 bg-[#1A1820] shadow-2xl"
           style={{ backgroundImage: "linear-gradient(135deg, rgba(255,45,122,0.08), rgba(0,224,199,0.06))" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="plyx-exit-modal-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-white/60 hover:text-white hover:bg-white/10 transition-colors"
        >
          <X className="w-4 h-4"/>
        </button>

        <h2 id="plyx-exit-title" className="font-display text-2xl text-white mb-1">
          Sad to see you go <span aria-hidden="true">👋</span>
        </h2>
        <p className="text-sm text-white/60 mb-5">
          One quick tap helps us shape Plyx for launch.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
              How was the preview?
            </label>
            <div className="flex gap-1.5" role="radiogroup" aria-label="Rate Plyx preview">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={score === n}
                  onClick={() => setScore(n)}
                  data-testid={`plyx-exit-score-${n}`}
                  className={`w-9 h-9 rounded-full text-sm font-bold transition-all ${
                    score === n
                      ? "bg-gradient-to-br from-[#FF2D7A] to-[#00E0C7] text-[#0A0A0A] scale-105"
                      : "bg-white/5 text-white/60 hover:bg-white/10"
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-white/40 mt-1.5 px-0.5">
              <span>Not for me</span>
              <span>Love it</span>
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wider text-white/50 mb-2">
              Anything specific? <span className="text-white/30 normal-case lowercase">(optional)</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {REASONS.map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setReason(reason === r ? null : r)}
                  data-testid={`plyx-exit-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    reason === r
                      ? "border-[#FF2D7A] bg-[#FF2D7A]/15 text-white"
                      : "border-white/15 text-white/70 hover:border-white/30"
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="plyx-exit-stay"
            className="px-4 py-2.5 rounded-full text-sm font-semibold text-white/80 hover:text-white border border-white/15 hover:border-white/30 transition-colors"
          >
            Stay in preview
          </button>
          <button
            type="button"
            onClick={submitAndExit}
            data-testid="plyx-exit-confirm"
            className="px-5 py-2.5 rounded-full text-sm font-bold text-[#0A0A0A]"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            Take me to AfriMarket →
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlyxExitModal;
